"use client"

import type { ComponentProps, ElementType, ReactNode, RefObject } from "react"
import { useEffect, useRef } from "react"
import cn from "classnames"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Lenis from "lenis"

import { Footer } from "~/components/elements/Footer/Footer"
import { useScroll } from "~/components/foundations/ScrollProvider"

import s from "./SmoothScrollWrapper.module.scss"

gsap.registerPlugin(ScrollTrigger)

type Props = {
  className?: string
  scrollWrapperRef?: RefObject<HTMLDivElement>
  isIn?: boolean
  children: ReactNode
} & ComponentProps<ElementType>

export const SmoothScrollWrapper = ({
  className,
  scrollWrapperRef,
  isIn = true,
  children,
}: Props) => {
  const { setLenis } = useScroll()

  const localRef = useRef<HTMLDivElement>(null)
  const wrapperRef = scrollWrapperRef ?? localRef
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isIn) document.documentElement.setAttribute("data-page-loaded", "true")
  }, [isIn])

  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return

    const lenisInstance = new Lenis({
      wrapper: wrapperRef.current,
      content: contentRef.current,
      easing: (t) => Math.min(1, 1.001 - Math.pow(3, -10 * t)),
    })

    setLenis(lenisInstance)

    // Update ScrollTrigger synchronously whenever Lenis moves, so scrubbed
    // animations stay frame-accurate with the rendered scroll position
    lenisInstance.on("scroll", ScrollTrigger.update)

    const ticker = (time: number) => {
      lenisInstance.raf(time * 1000)
    }

    gsap.ticker.add(ticker)
    // Disable lag smoothing: for scroll-linked animations the scrollbar is the
    // source of truth, so the timeline must follow it even after frame stalls
    gsap.ticker.lagSmoothing(0)

    const updateScrollbarWidth = () => {
      if (wrapperRef.current) {
        const scrollbarWidth =
          wrapperRef.current.offsetWidth - wrapperRef.current.clientWidth
        document.documentElement.style.setProperty(
          "--scrollbar-width",
          `${scrollbarWidth}px`,
        )
      }
    }
    updateScrollbarWidth()
    window.addEventListener("resize", updateScrollbarWidth)

    return () => {
      lenisInstance.off("scroll", ScrollTrigger.update)
      lenisInstance.destroy()
      gsap.ticker.remove(ticker)
    }
  }, [setLenis, wrapperRef])

  return (
    <div ref={wrapperRef} className={cn(s.contentWrapper, className)}>
      <div ref={contentRef}>
        {children}
        <Footer />
      </div>
    </div>
  )
}
